import React, { useMemo } from "react";
import { HighlightAlt } from "@mui/icons-material";
import { Alert, Box, Typography } from "@mui/material";

import { useGetVersionStatusQuery } from "fond/api";
import { getCurrentProjectData } from "fond/project";
import { LayerDeleteAction, LayerDrawAction, LayerPanelSection, LayerUploadAction } from "fond/project/panels/InputPanel/actions";
import LayerPanel from "fond/project/panels/InputPanel/LayerPanel";
import { deleteLayerFeatures, hasLayer, openUploadPanel, setActiveLayerGroupId, StatusTypes } from "fond/project/redux";
import { LayerId } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";

interface LayerGroupPanelProps {
  title: string;
  navigationKey: string;
  layerIds: LayerId[];
  /**
   * Input layer group whose area select and upload flows the panel uses; omitted for groups uploaded via `${navigationKey}-upload`.
   */
  inputLayerGroupId?: string;
}

/**
 * Entry panel for a planner extended layer group, offering import, draw and delete actions.
 */
const LayerGroupPanel: React.FC<LayerGroupPanelProps> = ({ title, navigationKey, layerIds, inputLayerGroupId }: LayerGroupPanelProps) => {
  const dispatch = useAppDispatch();
  const { open } = useStackedNavigationContext();
  const versionId = useAppSelector((state) => state.project.versionId);
  const projectData = useAppSelector((state) => getCurrentProjectData(state.project));
  const { data: status } = useGetVersionStatusQuery(versionId, { skip: !versionId || !inputLayerGroupId });

  const hasData = layerIds.some((layerId) => hasLayer(projectData, layerId));

  const handleAreaSelect = () => {
    dispatch(setActiveLayerGroupId(inputLayerGroupId));
    open("area-select");
  };

  const handleUpload = () => {
    if (inputLayerGroupId) {
      dispatch(openUploadPanel(inputLayerGroupId, status?.Status === StatusTypes.Complete));
      open("upload");
    } else {
      open(`${navigationKey}-upload`);
    }
  };

  const handleDelete = async () => {
    await dispatch(deleteLayerFeatures(layerIds, navigationKey));
  };
  const InfoText = useMemo(() => {
    if (navigationKey === "extended-design-aerial") {
      return "All possible locations where aerial fiber cables and devices may be placed in the Design step.";
    } else if (navigationKey === "extended-design-underground") {
      return "All possible locations where underground fiber cables and devices may be placed in the Design step.";
    } else if (navigationKey === "extended-design-leadins") {
      return "The approximate service path from the main network to an address location.";
    } else {
      return false;
    }
  }, [navigationKey]);
  return (
    <LayerPanel title={title} layerKey={navigationKey}>
      <Box px={1}>
        {InfoText && (
          <Alert severity="info" sx={{ mb: 2 }} variant="compact" data-testid="info-alert">
            <Typography variant="body3" component="p">
              {InfoText}
            </Typography>
          </Alert>
        )}
        <LayerPanelSection title="Import data">
          {inputLayerGroupId && (
            <StackedNavigationButton
              data-testid={`area-select-${navigationKey}-button`}
              icon={<HighlightAlt />}
              title="Area Select"
              subtitle="Use FOND data"
              onClick={handleAreaSelect}
            />
          )}
          <LayerUploadAction onClick={handleUpload} />
        </LayerPanelSection>
        <LayerPanelSection title="Manual selection and edit">
          <LayerDrawAction />
        </LayerPanelSection>
        {hasData && (
          <LayerPanelSection title="Remove existing data">
            <LayerDeleteAction onDelete={handleDelete} />
          </LayerPanelSection>
        )}
      </Box>
    </LayerPanel>
  );
};

export default LayerGroupPanel;
